/* eslint-disable @typescript-eslint/no-explicit-any */

import { TotalViewLayout } from "@streamllc/shared/ocb"

import { StreamAPI } from "@/api/stable/APIStable"
import FileUtils from "@/utils/transform/file"
import FormDataEnhanced from "@/utils/transform/FormDataEnhanced"

import { Scene } from "./scene.types"
import { UpdateScene } from "./scene.types"

import DAO from "../dao"
import ToastResult from "../ToastResult.decorator"

class SceneDAO extends DAO {
  @ToastResult("Scene", "Create", "Created")
  async create(name: string, company: string, orderScreen: any, welcomeScreen: any): Promise<any> {
    const response = await StreamAPI.fetch.POST["/scenes"]({
      body: { name, company, orderScreen, welcomeScreen }
    })

    const location = response.payload
    return location
  }

  async find(companyId: string): Promise<Scene[]> {
    const response = await StreamAPI.fetch.GET[`/scenes/company/{companyId}`]({
      pathParams: { companyId },
    })

    const transformedScenes = await Promise.all(
      response.payload.map((scene: any) => this.resolveSceneURLs(scene)),
    )
    return transformedScenes
  }

  async findOne(companyId: string, sceneId: string): Promise<Scene | undefined> {
    const response = await StreamAPI.fetch.GET[`/scenes/company/{companyId}`]({
      pathParams: { companyId },
    })

    const rawScene = response.payload.find((scene: any) => scene._id === sceneId)
    if (!rawScene) return undefined

    return this.resolveSceneURLs(rawScene)
  }

  private async resolveSceneURLs(scene: any): Promise<Scene> {
    const views = {
      order: scene.orderScreen,
      welcome: scene.welcomeScreen,
      totalScreen: scene.totalScreen,
    }

    const [order, welcome, totalScreen] = await Promise.all([
      FileUtils.resolveURLs(views.order ?? {}),
      FileUtils.resolveURLs(views.welcome ?? {}),
      FileUtils.resolveURLs(views.totalScreen ?? {}),
    ])

    return {
      ...scene,
      views: {
        order,
        welcome,
        totalScreen,
      },
    }
  }
  
  


  @ToastResult("Scene", "Update", "Updated")
  async update(sceneId: string, data: UpdateScene) {
    const order = data.orderScreen
    const welcome = data.welcomeScreen
    const totalScreen = data.totalScreen

    totalScreen.layout = TotalViewLayout.Layout1

    const { backgrounds: welcomeBackgrounds, logo: welcomeLogo, videos: welcomeVideos, ...welcomeRest } = welcome ?? {}
    const { backgrounds: orderBackgrounds, logo: orderLogo, ...orderRest } = order ?? {}
    const { backgrounds: totalScreenBackgrounds, video: totalScreenVideo, ...totalScreenRest } = totalScreen ?? {}

    const formData = new FormDataEnhanced({
      // General
      name: data.name,
      company: data.company,
    
      // Welcome View
      welcomeScreen: welcomeRest,
      logo: welcomeLogo,
      video: welcomeVideos,
      welcome: welcomeBackgrounds,
    
      // Order View
      orderScreen: orderRest,
      orderLogo,
      order: orderBackgrounds,

      // Total View
      totalScreen: totalScreenRest,
      totalBgs: totalScreenBackgrounds,
      totalVideo: totalScreenVideo,
    })
    

    await formData.renameFilesToHashes()

    const response = await StreamAPI.fetch.PATCH[`/scenes/{sceneId}`]({
      pathParams: { sceneId },
      body: formData
    })
    return response.payload
  }

  @ToastResult("Screen", "Assignment", "success")
  async assign(
    ...args: [string, string[], string[]]
  ): Promise<any> {
    const [sceneId, ocbIdsToAssign, ocbIdsToDetach] = args
    const response = await StreamAPI.fetch.POST[`/ocbapp/scenes/assign/{sceneId}`]({
      pathParams: { sceneId },
      body: { ocbIdsToAssign, ocbIdsToDetach }
    })
    return response.payload
  }


  @ToastResult("Scene", "Delete", "Deleted")
  async delete(sceneId: string) {
    await StreamAPI.fetch.DELETE[`/scenes/name/{sceneId}`]({
      pathParams: { sceneId }
    })
  }

  @ToastResult("Scene", "Rename", "Renamed")
  async updateName(sceneId: string, name: string) {
    const response = await StreamAPI.fetch.PATCH[`/scenes/name/{sceneId}`]({
      pathParams: { sceneId },
      body: { name }
    })
    return response.payload
  }
}

const SceneDao = new SceneDAO()
export default SceneDao
